import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import { withTranslation } from "react-i18next";

import Drawer from "components/Drawer";
import Loading from "components/Loading";
import GroupInfo from "components/Groups/GroupInfo";
import GroupEmployees from "components/Groups/GroupEmployees";
import GroupSupervisors from "components/Groups/GroupSupervisors";
import GroupStaffingLevels from "components/Groups/GroupStaffingLevels";

import { groupsService, employeesService } from "services";
import * as groupsActions from "actions/groupsActions";
import * as calendarsActions from "actions/calendarsActions";
import * as employeesActions from "actions/employeesActions";

import "./GroupEditDrawer.scss";

const TAB_DEFINITIONS = [
	{ eventKey: "info", labelKey: "groups.tabs.info" },
	{ eventKey: "empleados", labelKey: "groups.tabs.employees" },
	{ eventKey: "supervisores", labelKey: "groups.tabs.supervisors" },
	{ eventKey: "necesidades", labelKey: "groups.tabs.staffing" },
];

async function resolveGroupAssignments(employees, groupId) {
	return Promise.all(
		employees.map(async (employee) => {
			const currentAssignment = employee.employee_with_group;
			const assignmentGroupId =
				currentAssignment && currentAssignment.group
					? currentAssignment.group.id
					: null;

			if (Number(assignmentGroupId) === Number(groupId)) {
				return employee;
			}

			const employeeGroupsResponse = await employeesService.getGroups(
				employee.id
			);
			const employeeGroups = Array.isArray(employeeGroupsResponse)
				? employeeGroupsResponse
				: employeeGroupsResponse.ok &&
				  Array.isArray(employeeGroupsResponse.data)
					? employeeGroupsResponse.data
					: [];

			if (employeeGroups.length === 0) {
				return employee;
			}

			const groupAssignment = employeeGroups
				.filter(
					(assignment) =>
						assignment.group &&
						Number(assignment.group.id) === Number(groupId)
				)
				.sort(
					(a, b) =>
						moment(b.start_day).valueOf() - moment(a.start_day).valueOf()
				)[0];

			return groupAssignment
				? { ...employee, employee_with_group: groupAssignment }
				: employee;
		})
	);
}

class GroupEditDrawerContent extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: true,
			group: props.group && props.group.id ? props.group : {},
			group_employees: [],
			activeTab: props.initialTab || "info",
		};
	}

	componentDidMount() {
		this.loadGroupData();
	}

	componentDidUpdate(prevProps) {
		const prevId = prevProps.groupId || (prevProps.group && prevProps.group.id);
		const currId = this.props.groupId || (this.props.group && this.props.group.id);

		if (currId && prevId !== currId) {
			this.setState(
				{
					loading: true,
					group: this.props.group || {},
					group_employees: [],
					activeTab: this.props.initialTab || "info",
				},
				() => this.loadGroupData()
			);
			return;
		}

		if (
			this.props.initialTab &&
			prevProps.initialTab !== this.props.initialTab &&
			this.props.initialTab !== this.state.activeTab
		) {
			this.setState({ activeTab: this.props.initialTab });
		}
	}

	loadGroupData = async () => {
		const groupId = this.props.groupId || (this.props.group && this.props.group.id);
		const { t } = this.props;

		if (!groupId) {
			this.setState({ loading: false });
			return;
		}

		const group_employees_response = await employeesService.getAll({
			history_group_id: groupId,
			variant: "detailed",
		});
		const group_employees_response_data = group_employees_response.ok
			? group_employees_response.data
			: [];
		const group_employees = await resolveGroupAssignments(
			group_employees_response_data,
			groupId
		);

		const group_from_list = await groupsService.getAll({
			id: groupId,
			variant: "detailed",
		});

		if (
			group_from_list.ok &&
			group_from_list.data &&
			group_from_list.data.length > 0
		) {
			this.props.getSelectableCalendars();
			this.props.getChildGroups();
			this.props.getSelectableEmployees({ enabled: true });
			this.props.getGroupStaffingLevels(groupId);
			this.props.getSelectableStaffingLevels();

			const group = group_from_list.data[0];
			this.setState({
				group,
				group_employees,
				loading: false,
			});
			if (typeof this.props.onGroupUpdated === "function") {
				this.props.onGroupUpdated(group);
			}
		} else {
			this.setState({ loading: false });
			toastr.error(t("common.error"), t("groups.errorLoadingGroup"));
		}
	};

	getGroupEmployees = async () => {
		const groupId = this.state.group && this.state.group.id;
		if (!groupId) return;

		if (typeof this.props.onDataChanged === "function") {
			this.props.onDataChanged();
		}

		const group_employees_response = await employeesService.getAll({
			history_group_id: groupId,
			variant: "detailed",
		});
		const group_employees_response_data = group_employees_response.ok
			? group_employees_response.data
			: [];
		const group_employees = await resolveGroupAssignments(
			group_employees_response_data,
			groupId
		);

		this.setState({ group_employees });
	};

	updateGroup = (updatedGroup) => {
		this.setState({ group: updatedGroup });
		if (typeof this.props.onDataChanged === "function") {
			this.props.onDataChanged();
		}
		if (typeof this.props.onGroupUpdated === "function") {
			this.props.onGroupUpdated(updatedGroup);
		}
	};

	handleTabSelect = (eventKey) => {
		this.setState({ activeTab: eventKey });
	};

	render() {
		const { loading, group, group_employees, activeTab } = this.state;
		const { t } = this.props;
		const currentTab =
			TAB_DEFINITIONS.find((tab) => tab.eventKey === activeTab) ||
			TAB_DEFINITIONS[0];

		if (loading) {
			return (
				<Drawer.Main>
					<Drawer.MainHeader title={t("groups.editGroup")} />
					<Drawer.Body>
						<Loading />
					</Drawer.Body>
				</Drawer.Main>
			);
		}

		if (!group || !group.id) {
			return (
				<Drawer.Main>
					<Drawer.MainHeader title={t("groups.editGroup")} />
					<Drawer.Body>
						<p>{t("groups.errorLoadingGroup")}</p>
					</Drawer.Body>
					<Drawer.Footer>
						<button
							type="button"
							onClick={this.props.handleClose}
							className="btn btn-outline-primary"
						>
							{t("common.close")}
						</button>
					</Drawer.Footer>
				</Drawer.Main>
			);
		}

		return (
			<Drawer.Tabs activeKey={activeTab} onSelect={this.handleTabSelect}>
				<Drawer.Sidebar className="group-edit-drawer-sidebar" width={280}>
					<Drawer.SidebarHeader
						title={t("groups.editGroup")}
						subtitle={group.name}
					/>
					<Drawer.Nav>
						{TAB_DEFINITIONS.map((tab) => (
							<Drawer.NavItem key={tab.eventKey} eventKey={tab.eventKey}>
								{t(tab.labelKey)}
							</Drawer.NavItem>
						))}
					</Drawer.Nav>
				</Drawer.Sidebar>

				<Drawer.Main>
					<Drawer.MainHeader title={currentTab ? t(currentTab.labelKey) : ""} />
					<Drawer.Body className="group-edit-drawer-body">
						<Drawer.TabPane eventKey="info">
							<GroupInfo
								group={group}
								updateGroup={this.updateGroup}
								variant="drawer"
							/>
						</Drawer.TabPane>
						<Drawer.TabPane eventKey="empleados">
							<GroupEmployees
								group_employees={group_employees}
								group={group}
								getGroupEmployees={this.getGroupEmployees}
							/>
						</Drawer.TabPane>
						<Drawer.TabPane eventKey="supervisores">
							<GroupSupervisors group={group} />
						</Drawer.TabPane>
						<Drawer.TabPane eventKey="necesidades">
							<GroupStaffingLevels
								group={group}
								variant="drawer"
								onDataChanged={this.props.onDataChanged}
								autoOpenCreateNeed={this.props.autoOpenCreateNeed}
							/>
						</Drawer.TabPane>
					</Drawer.Body>
				</Drawer.Main>
			</Drawer.Tabs>
		);
	}
}

const mapDispatchToProps = (dispatch) => ({
	getSelectableCalendars: () =>
		dispatch(calendarsActions.getSelectableCalendars()),
	getChildGroups: () => dispatch(groupsActions.getChildGroups()),
	getGroupStaffingLevels: (groupId) =>
		dispatch(groupsActions.getGroupStaffingLevels(groupId)),
	getSelectableStaffingLevels: () =>
		dispatch(groupsActions.getSelectableStaffingLevels()),
	getSelectableEmployees: (options) =>
		dispatch(employeesActions.getSelectableEmployees(options)),
});

const ConnectedGroupEditDrawerContent = compose(
	connect(null, mapDispatchToProps),
	withTranslation()
)(GroupEditDrawerContent);

const GroupEditDrawer = ({
	isOpen,
	onClose,
	group,
	groupId,
	initialTab,
	autoOpenCreateNeed = false,
	width = 1000,
}) => {
	const hasDataChangesRef = React.useRef(false);
	const latestGroupRef = React.useRef(group);
	const drawerGroupKey = groupId || (group && group.id);

	React.useEffect(() => {
		if (isOpen) {
			hasDataChangesRef.current = false;
			latestGroupRef.current = group;
		}
	}, [isOpen, drawerGroupKey, group]);

	const markDataChanged = React.useCallback(() => {
		hasDataChangesRef.current = true;
	}, []);

	const handleGroupUpdated = React.useCallback((updatedGroup) => {
		latestGroupRef.current = updatedGroup;
	}, []);

	const handleClose = React.useCallback(() => {
		if (typeof onClose === "function") {
			onClose(hasDataChangesRef.current, latestGroupRef.current);
		}
	}, [onClose]);

	if (!isOpen) return null;

	return (
		<Drawer
			isOpen={isOpen}
			onClose={handleClose}
			width={width}
			className="group-edit-drawer"
		>
			<ConnectedGroupEditDrawerContent
				group={group}
				groupId={drawerGroupKey}
				initialTab={initialTab}
				autoOpenCreateNeed={autoOpenCreateNeed}
				handleClose={handleClose}
				onDataChanged={markDataChanged}
				onGroupUpdated={handleGroupUpdated}
			/>
		</Drawer>
	);
};

export default GroupEditDrawer;
